import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { AuthorCollectingSociety } from "../../../../api/graphql";
import { AuthorCollectingSocietyListStore } from "../../store";
import { collectiveManagementSocietyDisplayId } from "../../../collectiveManagementSociety/store/CollectiveManagementSocietyCommonStore";
import { territoryDisplayId } from "../../../territory/store/TerritoryCommonStore";

import { VisibilityIcon } from "../../../../icons";

export type GridExpressProps = { listStore?: AuthorCollectingSocietyListStore };

export const AuthorCollectingSocietyListForm: React.FC<GridExpressProps> =
  observer(({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<AuthorCollectingSociety>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: AuthorCollectingSociety, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<AuthorCollectingSociety>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "collectingSociety") {
          return {
            ...col,
            id: "collectingSociety",
            accessorFn: (row: AuthorCollectingSociety) =>
              row.collectingSociety
                ? collectiveManagementSocietyDisplayId(
                    row.collectingSociety,
                    langStore
                  )
                : "-",
          };
        }
        if (col.accessorKey === "territories") {
          return {
            ...col,
            id: "territories",
            accessorFn: (row: AuthorCollectingSociety) =>
              row.territories?.length
                ? row.territories
                    .map((x: any) => territoryDisplayId(x, langStore))
                    .join("\n")
                : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.authorCollectingSocietyCurrentPage || [];

    return (
      <div id={"ListOfAuthorCollectingSociety"}>
        <div id={"StartOfListingOfAuthorCollectingSociety"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  });
